import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';
import TimeEntryModal from './TimeEntryModal';

const { FiClock, FiPlus, FiTrash2, FiEdit2, FiFilter, FiUser, FiBriefcase, FiCalendar } = FiIcons;

const Timesheets = () => {
  const { timeEntries, deleteTimeEntry, projects, team } = useData();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [filterProject, setFilterProject] = useState('All');
  const [filterTeam, setFilterTeam] = useState('All');

  const filtered = useMemo(() => {
    return timeEntries.filter(te => {
      const matchProj = filterProject === 'All' || te.projectId === filterProject;
      const matchTeam = filterTeam === 'All' || te.teamMemberId === filterTeam;
      return matchProj && matchTeam;
    });
  }, [timeEntries, filterProject, filterTeam]);

  const formatDuration = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}m`;
  };

  const totalSeconds = filtered.reduce((acc, curr) => acc + (curr.duration || 0), 0);

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tighter">Timesheets</h1>
          <p className="text-slate-400 font-medium">Professional time tracking and workload audit.</p>
        </div>
        <button 
          onClick={() => { setEditingEntry(null); setIsModalOpen(true); }}
          className="bg-blue-600 text-white px-8 py-4 rounded-3xl font-black shadow-xl shadow-blue-100 flex items-center gap-3 hover:bg-blue-700 transition-all"
        >
          <SafeIcon icon={FiPlus} /> Log Time
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-slate-900 p-8 rounded-[2.5rem] text-white shadow-xl">
          <div className="flex items-center gap-3 text-blue-400 mb-2">
            <SafeIcon icon={FiClock} />
            <span className="text-[10px] font-black uppercase tracking-widest">Total Tracked</span>
          </div>
          <p className="text-4xl font-black">{formatDuration(totalSeconds)}</p>
        </div>
        
        <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm flex flex-col justify-center">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Filter by Project</label>
          <select 
            className="bg-slate-50 border-none rounded-xl p-3 font-bold text-sm outline-none"
            value={filterProject}
            onChange={e => setFilterProject(e.target.value)}
          >
            <option value="All">All Projects</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm flex flex-col justify-center">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Filter by Team</label>
          <select 
            className="bg-slate-50 border-none rounded-xl p-3 font-bold text-sm outline-none"
            value={filterTeam}
            onChange={e => setFilterTeam(e.target.value)}
          >
            <option value="All">All Members</option>
            {team.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-[3rem] border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-50">
                <th className="px-8 py-6">Date</th>
                <th className="px-8 py-6">User</th>
                <th className="px-8 py-6">Project / Description</th>
                <th className="px-8 py-6">Duration</th>
                <th className="px-8 py-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.map(entry => {
                const member = team.find(m => m.id === entry.teamMemberId);
                const project = projects.find(p => p.id === entry.projectId);
                return (
                  <tr key={entry.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-2 font-bold text-slate-600 text-sm">
                        <SafeIcon icon={FiCalendar} className="text-blue-500" />
                        {new Date(entry.date).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                          <SafeIcon icon={FiUser} />
                        </div>
                        <span className="font-bold text-slate-900 text-sm">{member?.name || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <p className="font-black text-slate-900 text-sm mb-1">{project?.name || 'General'}</p>
                      <p className="text-xs text-slate-400 font-medium line-clamp-1">{entry.description}</p>
                    </td>
                    <td className="px-8 py-6">
                      <span className="bg-blue-50 text-blue-600 px-3 py-1 rounded-lg font-black text-xs">
                        {formatDuration(entry.duration)}
                      </span>
                    </td>
                    <td className="px-8 py-6 text-right">
                      <div className="flex justify-end gap-2">
                        <button 
                          onClick={() => { setEditingEntry(entry); setIsModalOpen(true); }}
                          className="p-2 text-slate-400 hover:text-blue-600 transition-colors"
                        >
                          <SafeIcon icon={FiEdit2} />
                        </button>
                        <button 
                          onClick={() => { if(window.confirm('Delete this log?')) deleteTimeEntry(entry.id); }}
                          className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                        >
                          <SafeIcon icon={FiTrash2} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="py-20 text-center">
              <SafeIcon icon={FiClock} className="text-6xl text-slate-100 mx-auto mb-4" />
              <p className="text-slate-400 font-bold">No time entries found matching filters.</p>
            </div>
          )}
        </div>
      </div>

      <TimeEntryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} entry={editingEntry} />
    </div>
  );
};

export default Timesheets;